import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Brain,
  Sparkles,
  FileText,
  Package,
  Shirt,
  InfinityIcon,
} from "lucide-react";
import { Card, CardContent } from "components/UI/Card";
import { easeIn, motion } from "framer-motion";
import AIConversation from "components/AIConversation/AIConversation";
import ScrollDownIndicator from "shared/ScrollDownIndicator/ScrollDownIndicator";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { openModal } from "store/slices/authModalSlice";
import {
  disableScrollEvents,
  enableScrollEvents,
} from "../../utils/lockScroll";
import { selectUser } from "../../store/slices/userSlice";
import { openVideoModal } from "@/store/slices/videoModalSlice";
import { Button } from "../UI/Buttons";
import { environment } from "@/environments/environment";
import { openAIModal } from "@/store/slices/aiModalSlice";
import { getUserId } from "@/services/cookieService";
import OverlayBanner from "../UI/OverlayBanner";

const tools = [
  {
    id: 1,
    icon: Brain,
    title: "ELVA",
    description:
      "Your intelligent gifting agent ready to listen, act, and simplify.",
    href: "/gift-finder",
    color: "bg-gradient-to-br from-blue-500 to-indigo-700",
  },
  {
    id: 2,
    icon: Sparkles,
    title: "Mockup Studio",
    description: "For visualizing branded gifts before choosing.",
    href: "/mockup-tool",
    color: "bg-gradient-to-br from-purple-500 to-pink-500",
  },
  {
    id: 3,
    icon: FileText,
    title: "Catalog Builder",
    description:
      "Select products and generate shareable, theme-based catalogs in minutes.",
    href: "/mockup-tool",
    color: "bg-gradient-to-br from-blue-500 to-purple-600",
  },
  {
    id: 4,
    icon: Package,
    title: "Bulk Enquiry",
    description:
      "For sending interest/requirements for many items in one go, without repeated effort.",
    href: "/mockup-tool",
    color: "bg-gradient-to-br from-green-500 to-teal-600",
  },
];

const placeholderSuggestions = [
  "Need 100 eco-friendly gifts under ₹50 for tech conference attendees...",
  "Looking for branded drinkware sets for onboarding kits...",
  "Suggest gifts for employees in tech startup...",
  "Need bulk gifting options for Diwali under ₹1000...",
];

const placeholderSuggestionsMobile = [
  "Need 100 eco-friendly gifts under ₹50...",
  "Looking for branded drinkware sets for onboarding kits...",
  "Suggest gifts for employees in tech startup...",
  "Need bulk gifting options for Diwali under ₹1000...",
];

const HeroAISection: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [typedPlaceholder, setTypedPlaceholder] = useState("");
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [mobilesuggestionIndex, setMobileSuggestionIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [initialQuery, setInitialQuery] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const userId = useSelector(getUserId);

  const location = useLocation();
  const openAiDialog = location.state?.openAiDialog || false;

  useEffect(() => {
    if (openAiDialog) {
      setTimeout(() => {
        setShowAIModal(true);
      }, 1000);

      navigate(location.pathname, { replace: true });
    }
  }, [openAiDialog, navigate, location.pathname]);

  //ELVA modal flow from offical site
  // useEffect(() => {
  //   const params = new URLSearchParams(location.search);
  //   const action = params.get("action");

  //   if (action === "elva") {
  //     window.history.replaceState({}, document.title, location.pathname);

  //     dispatch(
  //       openAIModal({
  //         userId: user?.id || null,
  //         initialQuery: "Hi, I’d like to find some gifts!",
  //       })
  //     );
  //   }
  // }, [location.search, dispatch, user]);

  const handleSearch = (e?: React.SyntheticEvent) => {
    if (!userId) {
      dispatch(openModal());
      return;
    }

    e?.preventDefault();
    setInitialQuery(searchQuery);
    setShowAIModal(true);
  };

  const handleCloseAIModal = () => {
    setShowAIModal(false);
    setSearchQuery("");
  };

  const handleProductsFound = (products: any[]) => {
    // Route to product results page or handle products
    navigate("/products", { state: { products } });
  };

  const onCardClick = (tool: any) => {
    if (tool.title === "ELVA") {
      // if (!userId) {
      //   dispatch(openModal());
      //   return;
      // }
      // setShowAIModal(true);
      return;
    } else {
      localStorage.setItem("tool", tool.title);
      navigate(`/products`);
    }
  };

  useEffect(() => {
    const current = placeholderSuggestions[suggestionIndex];
    let charIndex = 0;
    let typingInterval: NodeJS.Timeout;
    let waitTimeout: NodeJS.Timeout;

    const typeNextChar = () => {
      typingInterval = setInterval(() => {
        if (charIndex <= current.length) {
          setTypedPlaceholder(current.slice(0, charIndex));
          charIndex++;
        } else {
          clearInterval(typingInterval);
          waitTimeout = setTimeout(() => {
            setSuggestionIndex(
              (prev) => (prev + 1) % placeholderSuggestions.length
            );
          }, 2000);
        }
      }, 40);
    };

    typeNextChar();
    return () => {
      clearInterval(typingInterval);
      clearTimeout(waitTimeout);
    };
  }, [suggestionIndex]);

  useEffect(() => {
    const delayBetweenAnimations = 1000;
    const animationDuration = 9000;
    const totalCycleTime = animationDuration + delayBetweenAnimations;

    const timeout = setTimeout(() => {
      setMobileSuggestionIndex(
        (prev) => (prev + 1) % placeholderSuggestionsMobile.length
      );
    }, totalCycleTime);

    return () => clearTimeout(timeout);
  }, [mobilesuggestionIndex]);

  useEffect(() => {
    const resize = () => {
      setIsMobile(window.innerWidth < 425);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.4,
      },
    },
  };

  const wordVariants = {
    initial: { opacity: 0, y: 100 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" as const },
    },
  };

  const capsuleVariants = {
    initial: { opacity: 0, y: 100, scale: 0.8, rotateX: -90 },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: { duration: 1, ease: "easeOut" as const },
    },
  };

  const capsule3DRotation = {
    animate: {
      rotateY: [0, 25, 0, -25, 0],
      scale: [1, 1.05, 1, 1.05, 1],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut" as const,
        times: [0, 0.25, 0.5, 0.75, 1],
      },
    },
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  useEffect(() => {
    if (showAIModal) {
      disableScrollEvents();
    } else {
      enableScrollEvents();
    }

    return () => {
      enableScrollEvents();
    };
  }, [showAIModal]);

  return (
    <section className="pb-20 px-4 relative overflow-hidden mt-6 md:mt-10">
      {showAIModal && (
        <AIConversation
          initialQuery={initialQuery}
          userId={user?.id || null}
          onClose={handleCloseAIModal}
          onProductsFound={handleProductsFound}
        />
      )}
      <div className="max-w-4xl mx-auto text-center relative">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, ease: "easeOut" }}
          className="inline-flex items-center justify-center mb-6 px-8 py-2 rounded-full bg-primary-gradient text-white text-xs md:text-sm font-medium shadow cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() =>
            window.open(
              `${environment.OFFICIAL_SITE_URL}`,
              "_blank",
              "noopener,noreferrer"
            )
          }
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === "Enter" &&
            window.open(
              `${environment.OFFICIAL_SITE_URL}`,
              "_blank",
              "noopener,noreferrer"
            )
          }
        >
          <InfinityIcon className="h-6 w-6 mr-2" />
          Powered by 360customizer
        </motion.div>
        <br />

        <div className="perspective-[1200px] max-w-6xl mx-auto">
          <motion.div
            className="text-center"
            variants={containerVariants}
            initial="initial"
            animate="animate"
          >
            <motion.h1 className="text-3xl md:text-6xl font-black leading-tight mb-0 md:mb-8 font-inter">
              <motion.span
                className="relative inline-block px-2 mb-0 md:mb-4"
                initial={{ scale: 2.5, rotateY: 0, opacity: 0 }}
                animate={{ scale: 1, x: 2, rotateY: 360, opacity: 1 }}
                transition={{ duration: 1, ease: "easeOut" as const }}
              >
                <motion.span className="relative left-3 z-10 bg-blue-900 bg-clip-text text-transparent drop-shadow-2xl [transform-style:preserve-3d]">
                  S
                </motion.span>
              </motion.span>

              <motion.span
                className="inline-block px-2 bg-gradient-to-r from-blue-900 via-blue-900 to-blue-900 bg-clip-text text-transparent drop-shadow-lg pb-1"
                variants={wordVariants}
              >
                implify
              </motion.span>

              <motion.span
                className="inline-block px-2 py-2 bg-gradient-to-r from-blue-900 via-blue-900 to-blue-900 bg-clip-text text-transparent drop-shadow-lg"
                variants={wordVariants}
              >
                Gifting
              </motion.span>

              <br />

              <motion.span
                className="inline-block px-2 bg-gradient-to-r from-blue-900 via-blue-700 to-indigo-500 bg-clip-text text-transparent drop-shadow-lg"
                variants={wordVariants}
              >
                With
              </motion.span>

              {/* 3D Rotating Capsule with Smart AI */}
              <motion.span
                className="block md:inline-block mx-4 my-2"
                variants={capsuleVariants}
                style={{
                  transformStyle: "preserve-3d",
                  perspective: "1000px",
                }}
              >
                <motion.div
                  className="relative inline-block"
                  {...capsule3DRotation}
                  style={{
                    transformStyle: "preserve-3d",
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-full blur-sm opacity-75 scale-110"></div>

                  <div className="relative border-white/40 px-6 py-2 md:py-4 rounded-full bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-sm shadow-2xl">
                    <span className="bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent font-bold text-2xl md:text-5xl drop-shadow-lg">
                      Expertise
                    </span>
                  </div>

                  <div className="absolute -top-2 -left-2 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <div className="absolute -bottom-2 -right-2 w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-1000"></div>
                  <div className="absolute top-1/2 -left-4 w-1 h-1 bg-pink-400 rounded-full animate-pulse delay-500"></div>
                  <div className="absolute top-1/2 -right-4 w-1 h-1 bg-cyan-400 rounded-full animate-pulse delay-1500"></div>
                </motion.div>
              </motion.span>

              <motion.span
                className="px-2 bg-gradient-to-r from-indigo-600 via-blue-700 to-blue-900 bg-clip-text text-transparent drop-shadow-lg"
                variants={wordVariants}
              >
                You Deserve
              </motion.span>
            </motion.h1>
          </motion.div>
        </div>

        <motion.p
          initial={{
            opacity: 0,
            y: 30,
            scale: 0.9,
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              delay: 1.7,
              duration: 1,
              ease: "easeOut" as const,
            },
          }}
          className="text-md text-center text-slate-500 mb-12 max-w-2xl mx-auto"
        >
          Browse, visualize, and shortlist with ease our smart tools streamline
          the selection process, ensuring your corporate gifting is seamless
          from concept to delivery.
        </motion.p>

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
            scale: 0.9,
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              delay: 2,
              duration: 0.6,
              ease: "easeOut" as const,
            },
          }}
          onSubmit={handleSearch}
          className="w-full h-16 flex justify-center items-center"
        >
          <div className="w-full flex flex-wrap justify-center items-center gap-3">
            {/* <ButtonPrimary
              onClick={handleSearch}
              className="w-full sm:w-auto sm:max-w-none text-sm sm:text-base px-4 sm:px-6 py-3 sm:py-4 h-auto sm:h-14 min-h-[44px] md:h-14 md:w-60"
            >
              <Brain className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Ask AI</span>
            </ButtonPrimary> */}


            {/* Normal active button */}
            <ButtonPrimary
              onClick={() => handleNavigate("/products")}
              className="w-full sm:w-auto sm:max-w-none text-sm sm:text-base px-4 sm:px-6 py-3 sm:py-4 h-auto sm:h-14 min-h-[44px] md:h-14 md:w-60"
            >
              <Shirt className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Explore Products</span>
            </ButtonPrimary>
            <ButtonPrimary
              onClick={() => handleNavigate("/curated-kits")}
              className="w-full sm:w-auto sm:max-w-none text-sm sm:text-base px-4 sm:px-6 py-3 sm:py-4 h-auto sm:h-14 min-h-[44px] md:h-14 md:w-60"
            >
              <Package className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Explore Curated Kits</span>
            </ButtonPrimary>

          </div>
        </motion.div>
        <motion.div
          initial={{
            y: -20,
            opacity: 0,
          }}
          animate={{
            y: 0,
            opacity: 1,
            transition: {
              delay: 2,
              duration: 1,
              ease: easeIn,
            },
          }}
          className="flex justify-center items-center mt-5 md:mt-10"
        >
          <ScrollDownIndicator
            targetId="tools-section"
            className="text-white bg-gradient-to-b from-indigo-500 to-purple-400 hidden sm:block"
          />
        </motion.div>
      </div>

      {/* {/* Cards */}
      <div
        className="flex flex-col justify-center items-center pt-20 md:py-28"
        id="tools-section"
      >
        <div className="text-3xl mb-4 md:text-5xl bg-gradient-to-r from-blue-900 to-indigo-500 font-inter font-bold bg-clip-text text-transparent">
          Explore Our Tools
        </div>

        <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto text-center ">
          Unwrap joy with our thoughtfully designed tools made just for gifters
          like you. Explore, customize, and send smiles in just a few clicks!
        </p>
        <div className="grid mt-10 md:mt-28 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-20 mx-auto items-stretch">
          {tools.map((tool) => {
            const Icon = tool.icon;
            const hasVideoButton =
              tool.title === "Mockup Studio" ||
              tool.title === "Catalog Builder";
            const videoTab =
              tool.title === "Mockup Studio" ? "mockup" : "catalog-builder";

            const cardElement = (
              <Card className="h-full w-full max-w-xs md:w-60 flex flex-col justify-between  hover:border-white/50 shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105 bg-white/60 backdrop-blur-sm">
                <CardContent className="!p-0 md:!p-6 flex flex-row md:flex-col h-full">
                  <div
                    className={`basis-1/3 sm:basis-auto md:w-16 md:h-16 ${tool.color} rounded-l-2xl md:rounded-2xl flex items-center justify-center md:mx-auto md:group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="h-8 w-8 text-white" />
                  </div>

                  <div className="flex flex-col justify-between basis-2/3 sm:basis-auto p-4 text-left md:text-center flex-grow">
                    <div className="space-y-2">
                      <h3 className="font-bold text-sm md:text-lg mb-2 min-h-[24px]">
                        {tool.title}
                      </h3>
                      <p className="text-gray-600 text-xs md:text-sm min-h-[80px] md:min-h-[60px]">
                        {tool.description}
                      </p>
                    </div>
                  </div>

                  {hasVideoButton && (
                    <button
                      className="display-none md:display-block w-full mt-2 text-indigo-400 hover:text-indigo-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(openVideoModal(videoTab));
                      }}
                    >
                      {`See How It Works?`}
                    </button>
                  )}
                </CardContent>
              </Card>
            );

            return (
              <div
                key={tool.id}
                className="h-full group cursor-pointer"
                onClick={() => onCardClick(tool)}
              >
                {tool.title === "ELVA" ? (
                  <OverlayBanner message="Coming Soon">
                    {cardElement}
                  </OverlayBanner>
                ) : (
                  cardElement
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HeroAISection;
